import React, { useState, useEffect } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import {
  CssBaseline,
  Container,
  Typography,
  Box,
  Button,
  Grid,
  Card,
  Avatar,
  Divider,
  Stack,
} from '@mui/material';
import {
  Search,
  TrendingUp,
  Security,
  People,
  Business,
  Phone,
  Email,
  LocationOn,
  ArrowForward,
  CheckCircle
} from '@mui/icons-material';
import { AuthProvider } from '../context/AuthContext';
import Header from '../components/Header';
import LoginModal from '../modals/LoginModal';
import api from '../services/api';

const theme = createTheme({
  palette: {
    primary: {
      main: '#008080', // Deep Teal (from logo)
      light: '#4FB3B3',
      dark: '#005252',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#FFA500', // Warm Orange (from logo)
      light: '#FFBF73',
      dark: '#CC8400',
      contrastText: '#000000',
    },
    accent: {
      main: '#FFFF00', // Bright Yellow (from logo)
      light: '#FFFF66',
      dark: '#CCCC00',
      contrastText: '#000000',
    },
    grey: {
      main: '#808080', // Cool Gray (from logo)
      light: '#B3B3B3',
      dark: '#595959',
    },
    background: {
      default: '#f8f9fa',
      paper: '#ffffff',
    },
    text: {
      primary: '#2d3748',
      secondary: '#4a5568',
    },
    success: {
      main: '#008080', // Using teal for success
    },
    warning: {
      main: '#FFA500', // Using orange for warning
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '3.5rem',
      fontWeight: 700,
      lineHeight: 1.2,
    },
    h2: {
      fontSize: '2.5rem',
      fontWeight: 600,
      lineHeight: 1.3,
    },
    h3: {
      fontSize: '2rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 500,
      lineHeight: 1.4,
    },
  },
});

function LandingPage() {
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [loginModalUserType, setLoginModalUserType] = useState('investor');

  const [contactInfo, setContactInfo] = useState({
    phone: '(415) 555-0123',
    email: 'hello@offmarkets.com',
    address: 'San Francisco, CA',
    supportEmail: 'support@offmarkets.com',
    businessHours: 'Mon-Fri 9AM-6PM PST'
  });

  useEffect(() => {
    const fetchLandingData = async () => {
      try {
        const contactResponse = await api.landing.getContactInfo();
        setContactInfo(contactResponse.data);
      } catch (error) {
        console.error('Error fetching landing data:', error);
        // Keep default fallback data
      }
    };

    fetchLandingData();
  }, []);

  const handleBrowseProperties = () => {
    setLoginModalUserType('investor');
    setLoginModalOpen(true);
  };

  const handleBecomeWholesaler = () => {
    setLoginModalUserType('wholesaler');
    setLoginModalOpen(true);
  };

  const handleGetStarted = () => {
    setLoginModalUserType('investor');
    setLoginModalOpen(true);
  };

  const handleLearnMore = () => {
    // Scroll to the features section
    document.getElementById('features-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleStartSearch = () => {
    setLoginModalUserType('investor');
    setLoginModalOpen(true);
  };

  const handleQuickLink = (link) => {
    switch (link) {
      case 'Member Portal':
        handleBrowseProperties();
        break;
      case 'Wholesaler Application':
        handleBecomeWholesaler();
        break;
      case 'About Our Standards':
        // Scroll to value proposition section
        document.getElementById('value-proposition')?.scrollIntoView({ behavior: 'smooth' });
        break;
      case 'Contact':
        // Scroll to footer
        document.getElementById('footer')?.scrollIntoView({ behavior: 'smooth' });
        break;
      default:
        break;
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />

      <Header />

      {/* Hero Section */}
      <Box
        sx={{
          backgroundColor: '#008080', // Clean Deep Teal background
          color: 'white',
          py: { xs: 8, md: 12 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                variant="h1"
                component="h1"
                gutterBottom
                sx={{ fontSize: { xs: '2.2rem', sm: '2.8rem', md: '3.5rem' }, lineHeight: { xs: 1.15, md: 1.2 } }}
              >
                Invitation Only
                <Box component="span" sx={{ color: '#FFFF00' }}>
                  {' '}Off-Market{' '}
                </Box>
                Network
              </Typography>
              <Typography variant="h5" sx={{ mb: 4, opacity: 0.9, fontSize: { xs: '1rem', sm: '1.15rem', md: '1.5rem' } }}>
                By referral only. Join an elite network of sophisticated investors and verified wholesalers
                accessing premium opportunities before they reach the public market.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Button
                  variant="contained"
                  size="large"
                  sx={{
                    backgroundColor: '#FFA500',
                    color: 'black',
                    '&:hover': { backgroundColor: '#FFBF73' },
                    px: 4,
                    py: 1.5,
                  }}
                  endIcon={<Search />}
                  onClick={handleBrowseProperties}
                >
                  Request Invitation
                </Button>
                <Button
                  variant="contained"
                  size="large"
                  sx={{
                    backgroundColor: '#FFFF00',
                    color: 'black',
                    '&:hover': { backgroundColor: '#FFFF66' },
                    px: 4,
                    py: 1.5,
                  }}
                  endIcon={<Business />}
                  onClick={handleBecomeWholesaler}
                >
                  Apply for Membership
                </Button>
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  position: 'relative',
                  textAlign: 'center',
                }}
              >
                <Box
                  component="img"
                  src="/wholesalesd.png"
                  alt="Premium off-market real estate investment opportunities"
                  sx={{
                    width: '100%',
                    maxWidth: 500,
                    borderRadius: 2,
                    boxShadow: 3,
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Value Proposition Section */}
      <Box id="value-proposition" sx={{ py: { xs: 8, md: 12 }, backgroundColor: 'background.default' }}>
        <Container maxWidth="lg">
          <Typography variant="h2" align="center" gutterBottom>
            Elite Network Standards
          </Typography>
          <Typography variant="h5" align="center" sx={{ mb: 8, color: 'text.secondary' }}>
            Rigorous selection process ensures only qualified investors and proven wholesalers
          </Typography>

          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 3 }}>
                <Box sx={{ mb: 3 }}>
                  <Avatar sx={{ mx: 'auto', bgcolor: 'primary.main', width: 64, height: 64 }}>
                    <TrendingUp sx={{ fontSize: 32 }} />
                  </Avatar>
                </Box>
                <Typography variant="h4" gutterBottom>
                  Curated Opportunities
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Every deal is hand-selected by our team. Only the highest-quality off-market
                  properties that meet our strict investment criteria make it to our network.
                </Typography>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 3 }}>
                <Box sx={{ mb: 3 }}>
                  <Avatar sx={{ mx: 'auto', bgcolor: 'secondary.main', width: 64, height: 64 }}>
                    <Security sx={{ fontSize: 32 }} />
                  </Avatar>
                </Box>
                <Typography variant="h4" gutterBottom>
                  Selective Membership
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Our multi-stage vetting process includes background checks, track record verification,
                  and personal interviews. Only 3% of applicants are accepted into our network.
                </Typography>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 3 }}>
                <Box sx={{ mb: 3 }}>
                  <Avatar sx={{ mx: 'auto', bgcolor: 'primary.main', width: 64, height: 64 }}>
                    <People sx={{ fontSize: 32 }} />
                  </Avatar>
                </Box>
                <Typography variant="h4" gutterBottom>
                  Sophisticated Network
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Join a community of accredited investors and institutional-grade wholesalers.
                  Network with like-minded professionals who understand the nuances of off-market investing.
                </Typography>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Box id="features-section" sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h2" gutterBottom>
                Curated Deal Flow
              </Typography>
              <Typography variant="h5" sx={{ mb: 4, color: 'text.secondary' }}>
                Access pre-vetted opportunities that match your investment criteria and risk profile
              </Typography>

              <Stack spacing={2}>
                {[
                  'Personalized deal matching based on your investment profile',
                  'Comprehensive due diligence packages for every opportunity',
                  'Direct access to decision-makers and property owners',
                  'Priority notifications for time-sensitive opportunities',
                ].map((feature, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center' }}>
                    <CheckCircle sx={{ color: 'success.main', mr: 2 }} />
                    <Typography variant="body1">{feature}</Typography>
                  </Box>
                ))}
              </Stack>

              <Button
                variant="contained"
                size="large"
                sx={{ mt: 4 }}
                endIcon={<ArrowForward />}
                onClick={handleStartSearch}
              >
                Begin Application
              </Button>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box
                component="img"
                src="/api/placeholder/600/400"
                alt="Property search interface"
                sx={{
                  width: '100%',
                  borderRadius: 2,
                  boxShadow: 3,
                }}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{ py: { xs: 8, md: 12 }, backgroundColor: 'primary.main', color: 'white' }}>
        <Container maxWidth="md">
          <Typography variant="h2" align="center" gutterBottom>
            Join the Elite Circle
          </Typography>
          <Typography variant="h5" align="center" sx={{ mb: 6, opacity: 0.9 }}>
            Become part of an exclusive network where quality trumps quantity
          </Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} justifyContent="center">
            <Button
              variant="contained"
              size="large"
              sx={{
                backgroundColor: 'white',
                color: 'primary.main',
                '&:hover': { backgroundColor: '#f7fafc' },
                px: 6,
                py: 2,
              }}
              onClick={handleGetStarted}
            >
              Apply for Membership
            </Button>
            <Button
              variant="outlined"
              size="large"
              sx={{
                borderColor: 'white',
                color: 'white',
                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
                px: 6,
                py: 2,
              }}
              onClick={handleLearnMore}
            >
              Membership Criteria
            </Button>
          </Stack>
        </Container>
      </Box>

      {/* Footer */}
      <Box id="footer" sx={{ py: 6, backgroundColor: 'background.paper', borderTop: 1, borderColor: 'divider' }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Typography variant="h4" gutterBottom sx={{ color: 'primary.main' }}>
                OffMarkets
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Invitation-only network connecting elite investors with institutional-grade wholesalers.
              </Typography>
            </Grid>

            <Grid item xs={12} md={4}>
              <Typography variant="h6" gutterBottom>
                Quick Links
              </Typography>
              <Stack spacing={1}>
                {['Member Portal', 'Wholesaler Application', 'About Our Standards', 'Contact', 'Privacy Policy'].map((link) => (
                  <Typography
                    key={link}
                    variant="body2"
                    sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
                    onClick={() => handleQuickLink(link)}
                  >
                    {link}
                  </Typography>
                ))}
              </Stack>
            </Grid>

            <Grid item xs={12} md={4}>
              <Typography variant="h6" gutterBottom>
                Contact Us
              </Typography>
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Phone sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="body2">{contactInfo.phone}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Email sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="body2">{contactInfo.email}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <LocationOn sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="body2">{contactInfo.address}</Typography>
                </Box>
              </Stack>
            </Grid>
          </Grid>

          <Divider sx={{ my: 4 }} />
          <Typography variant="body2" align="center" color="text.secondary">
            © 2024 OffMarkets. All rights reserved.
          </Typography>
        </Container>
      </Box>

      <LoginModal
        open={loginModalOpen}
        onClose={() => setLoginModalOpen(false)}
        defaultUserType={loginModalUserType}
      />
    </ThemeProvider>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <LandingPage />
    </AuthProvider>
  );
}
